import { Alert, Box } from "@mui/material";
import { getRoles, useAuth } from "@ssf/auth-components";
import {
  ContentContainer,
  GoldThemeProvider,
  goldBgImage,
  isGoldClub,
  Loading,
} from "@ssf/components";
import { RoleType } from "@ssf/memdb-api";
import { type ReactElement, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import type { UseQueryClub } from "../lib/index.js";
import { useParams, useQueryClub } from "../lib/index.js";
import {
  GoldCongratsDialog,
  shouldShowGoldCongrats,
} from "./GoldCongratsDialog.js";

export const ClubGuard = ({
  children,
}: {
  children: (club: UseQueryClub) => ReactElement;
}) => {
  const { t } = useTranslation("ClubGuard");
  const { user, isFedAdmin } = useAuth();
  const { clubId } = useParams<"club">();

  const { data: club, isLoading } = useQueryClub(clubId);

  const hasGoldStatus = !!(club && isGoldClub(club));
  const [congratsOpen, setCongratsOpen] = useState(false);

  useEffect(() => {
    if (shouldShowGoldCongrats(hasGoldStatus)) {
      setCongratsOpen(true);
    }
  }, [hasGoldStatus]);

  if (isLoading) {
    return <Loading />;
  }

  if (!club) {
    return (
      <ContentContainer sx={{ py: 7 }}>
        <Alert severity="info">{t("noClub")}</Alert>
      </ContentContainer>
    );
  }

  if (!user) {
    return (
      <ContentContainer sx={{ py: 7 }}>
        <Alert severity="info">{t("noUser")}</Alert>
      </ContentContainer>
    );
  }

  const isDistrictAdmin = getRoles(user).includes(
    `district:${club.district.id}`,
  );
  const isAdmin =
    club.roles.some(
      ({ orgrole, end, memberid }) =>
        orgrole === RoleType.Admin &&
        end === "1970-01-01" &&
        memberid === Number(user.profile.sub),
    ) ||
    isDistrictAdmin ||
    isFedAdmin;

  if (!isAdmin) {
    return (
      <ContentContainer sx={{ py: 7 }}>
        <Alert severity="info">{t("notAdmin")}</Alert>
      </ContentContainer>
    );
  }

  return (
    <GoldThemeProvider enabled={hasGoldStatus} backgroundImage={goldBgImage}>
      <Box sx={{ minHeight: "100%" }}>{children(club)}</Box>
      <GoldCongratsDialog
        open={congratsOpen}
        onClose={() => setCongratsOpen(false)}
      />
    </GoldThemeProvider>
  );
};
